import {
  useGetOrdersOrderIdTransferPayment,
  usePostOrdersOrderIdTransferPayment,
} from '@agos/api-client/orders'
import { useGetOrdersOrderIdQrPaymentData } from '@agos/api-client/payments'
import { apiErrorMessage } from '@agos/api-client'
import { T, codeModeOf, gatewayName, tt, useModalFocus } from '@agos/shared'
import { Spinner, cn, useToast } from '@agos/ui'
import { AlertTriangle, CheckCircle2, X } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  askNote,
  countdown,
  headline,
  keepPolling,
  showsCode,
  transferStateOf,
  waitingNote,
} from './transfer'

/**
 * The transfer sheet at a door - `FRONTEND.md` §9.338.
 *
 * **The owner:** *"instead of 'Nezaplatil, Zaplatil', can be only closed and will show šofér thing
 * like… 'waiting for payment'"*. So there is one button and it closes the sheet. The bank decides
 * whether the money arrived; FB-186 recorded what happened when the driver did - their word was
 * stored identically to a verified payment, because the column could not tell them apart.
 *
 * **The QR is the gateway's own page.** The customer scans it, their browser opens TatraPay, they
 * pick their bank and pay. A SEPA payload would be readable by more apps and is what this sheet drew
 * before - and nothing would ever tell us it had been paid, which is the whole of the request.
 */
const POLL_MS = 3000

export function TransferPaymentSheet(props: {
  orderId: number
  orderNumber: string
  onClose: () => void
  onPaid: () => void
}) {
  const { toast } = useToast()

  /**
   * Opened once, on mount.
   *
   * **A `POST` from an effect is unusual and it is what the owner asked for**: *"when šofér will
   * click on 'Zaplatiť prevodom'"* is the click, and this sheet exists only as its consequence. The
   * ref is what keeps a re-render from opening a second one - and the server is the real guard, since
   * it returns the transfer already open rather than creating another session.
   */
  const opened = useRef(false)
  const open = usePostOrdersOrderIdTransferPayment({
    mutation: {
      onError: (caught) => {
        // §9.379, FB-674 - **the server's own sentence.** The owner reported *"platba QR code in
        // driver app, we received error - Prevod sa nepodarilo založiť"*, and that title is all this
        // handler has ever said: the endpoint refuses for reasons that matter and name themselves -
        // *"Táto objednávka už je zaplatená - prevod by vzal peniaze druhýkrát"*, a gateway that
        // would not open a session - and the driver was told none of them. §9.358's rule, and the
        // reason §9.365 exists for thirteen other screens.
        toast({
          variant: 'destructive',
          title: tt('Prevod sa nepodarilo založiť'),
          description: apiErrorMessage(caught, tt('Server neodpovedal.')),
        })
        props.onClose()
      },
    },
  })

  useEffect(() => {
    if (opened.current) return
    opened.current = true
    open.mutate({ orderId: props.orderId })
    // `open` is a fresh object each render; the ref above is the guard, not the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.orderId])

  const poll = useGetOrdersOrderIdTransferPayment(props.orderId, {
    query: { enabled: open.isSuccess, refetchInterval: POLL_MS },
  })

  // The `POST` answers the same shape as the `GET`, so the sheet has a state to draw before the
  // first poll lands rather than a spinner over a code the server has already created.
  const state = useMemo(
    () => transferStateOf(poll.data) ?? transferStateOf(open.data),
    [poll.data, open.data],
  )

  // Nothing changes once it has settled, so the poll stops. A driver's phone on a mobile connection
  // should not be asking every three seconds about a payment that is finished.
  useEffect(() => {
    if (state !== null && !keepPolling(state)) {
      if (state.phase === 'paid') props.onPaid()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.phase])

  const settled = state !== null && !keepPolling(state)

  /**
   * §9.389 - the payload a banking app reads, and whether the bank will take it.
   *
   * `qr-payment-data` is the endpoint that has served this since §9.6: the SEPA string, the amount,
   * the account name, and `payable`/`payableReason` from FB-186's mod-97 check on the stored IBAN.
   * Read here rather than derived, because whether an IBAN is payable is the server's answer.
   */
  const { data: qrData } = useGetOrdersOrderIdQrPaymentData(props.orderId)
  const sepa = (qrData ?? null) as {
    qrString?: unknown
    payable?: unknown
    payableReason?: unknown
  } | null

  /**
   * §9.402 - which of the two codes this franchise draws, chosen at *Nastavenia rozvozu - QR
   * Platba*.
   *
   * **`bank` when the answer has not arrived yet**, which is `codeModeOf`'s rule for anything it
   * does not recognise: the endpoint refuses outright for an unconfigured franchise, and falling
   * back to the mode that ships is better than drawing the gateway's URL at somebody who chose the
   * transfer.
   */
  const codeMode = codeModeOf(qrData)

  /**
   * What the code encodes, or null when there is nothing to draw.
   *
   * One value for both the inline code and the zoomed one, so the two cannot diverge - they did not
   * before only because both repeated the same three-part condition.
   */
  const codeValue =
    codeMode === 'gateway'
      ? typeof state?.qrUrl === 'string' && state.qrUrl !== ''
        ? state.qrUrl
        : null
      : sepa?.payable === true && typeof sepa.qrString === 'string'
        ? sepa.qrString
        : null

  const [zoomed, setZoomed] = useState(false)
  // The largest square the viewport allows, measured once per open rather than watched: a driver
  // does not rotate the phone between showing a code and the customer scanning it.
  const zoomSize = Math.max(
    220,
    Math.min(
      typeof window === 'undefined' ? 320 : window.innerWidth - 32,
      typeof window === 'undefined' ? 320 : window.innerHeight - 32,
    ),
  )

  /* §9.477 - §9.441's three keyboard behaviours, which this sheet's `aria-modal` promised and did not
     keep. The container is the `<section>` the role is on, so the dismiss scrim outside it stays
     clickable and stays out of the tab ring - §9.469's own reason for putting the role there. */
  const panelRef = useRef<HTMLElement>(null)
  // Unconditional: this sheet displays a QR code and submits nothing, so there is no moment when
  // dismissing it loses anything - §9.480.
  const { onKeyDown } = useModalFocus(true, panelRef, props.onClose)

  return (
    <div className="fixed inset-0 z-50 flex items-end" data-transfer-sheet>
      <button
        type="button"
        className="absolute inset-0 bg-black/60"
        aria-label={tt('Zavrieť')}
        onClick={props.onClose}
      />
      <section
        ref={panelRef}
        onKeyDown={onKeyDown}
        role="dialog"
        aria-modal="true"
        /* §9.469 - a driver takes a bank transfer for an order here, and it did not say it was a dialog.
         *
         * **On the `<section>` rather than on the wrapper, unlike the four modals.** The wrapper
         * also holds the scrim `<button className="absolute inset-0">` that dismisses this sheet,
         * and a role there would put the dismiss control inside the dialog it dismisses. */
        aria-labelledby="transfer-sheet-title"
        className="relative w-full max-h-[92dvh] overflow-y-auto rounded-t-3xl bg-background border-t border-border p-4 space-y-4"
      >
        <div className="flex items-center justify-between gap-3">
          <h2 id="transfer-sheet-title" className="text-base font-bold">
            <T>QR Pay</T> · #{props.orderNumber}
          </h2>
          <button
            type="button"
            onClick={props.onClose}
            aria-label={tt('Zavrieť')}
            /* §9.477 - where the opening focus lands when nothing above it is a field. */
            data-dialog-cancel
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {state === null ? (
          <div className="flex justify-center py-10" data-transfer-opening>
            <Spinner />
          </div>
        ) : (
          <>
            {showsCode(state) && (
              <div className="space-y-3">
                {/* §9.389, FB-692 - **the code is a SEPA transfer, not a page URL.** The owner
                  * scanned this and it opened the TatraPay app: *"that QR code should be scannable
                  * for bank app"*. `qrString` is EPC069-12 - the eleven lines every Slovak banking
                  * app reads - and it pre-fills the payee, the amount and *Objednavka N*. §9.338
                  * replaced this payload to work around an IBAN that fails mod-97, which is a
                  * setting rather than a reason to hand the customer something else. */}
                {/* §9.402 - and which of the two payloads becomes the code is now a setting. In
                  * gateway mode `payable` is deliberately not consulted: it is a fact about the
                  * stored IBAN, and the gateway's page never reads one. */}
                {codeValue !== null ? (
                  <>
                    <button
                      type="button"
                      onClick={() => setZoomed(true)}
                      aria-label={tt('Zväčšiť QR kód')}
                      className="w-full flex justify-center rounded-2xl bg-white p-4"
                      data-transfer-code
                      data-transfer-code-mode={codeMode}
                    >
                      {/* `level="M"`: a SEPA payload is shorter than a URL, and the code still has
                        * to scan off a phone held at arm's length in the dark. */}
                      <QRCodeSVG value={codeValue} size={220} level="M" />
                    </button>
                    <p className="text-center text-[11px] text-muted-foreground">
                      {codeMode === 'gateway' ? (
                        // §9.795 - the gateway the franchise takes cards through, which may be GP webpay.
                        <>
                          <T>Kód otvorí platobnú stránku</T> {gatewayName(state.gateway)}.{' '}
                          <T>Ťuknutím ho zväčšíte.</T>
                        </>
                      ) : (
                        <T>Kód otvorí prevod v bankovej aplikácii. Ťuknutím ho zväčšíte.</T>
                      )}
                    </p>
                  </>
                ) : (
                  /* §9.389, FB-186 - **no code rather than one the bank refuses.** The server
                   * answers `payable` and says why; a QR that fails at a customer's door while the
                   * driver stands there is worse than none. */
                  <p
                    className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs font-semibold"
                    data-transfer-unpayable
                  >
                    {typeof sepa?.payableReason === 'string' && sepa.payableReason !== ''
                      ? sepa.payableReason
                      : tt('QR platba nie je nastavená - použite odkaz nižšie.')}
                  </p>
                )}
                <p className="text-center text-sm font-semibold" data-transfer-ask>
                  {askNote(state, codeMode)}
                </p>
                {/* §9.389 - the gateway session keeps its job and loses the QR: paying by card on
                  * the bank's own page is a real option, and the session is how the money is
                  * verified. What it is not is something to photograph. */}
                {codeMode === 'bank' && typeof state.qrUrl === 'string' && state.qrUrl !== '' && (
                  <a
                    href={state.qrUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-transfer-link
                    className="block w-full min-h-[44px] rounded-2xl border border-border text-center text-xs font-semibold leading-[44px]"
                  >
                    <T>Zaplatiť kartou cez odkaz</T>
                  </a>
                )}
              </div>
            )}

            {/* §9.389, FB-693 - *"when I click on that QR code it can be zoomed in"*. A customer
              * holds their own phone over somebody else's, at arm's length, outdoors. */}
            {zoomed && codeValue !== null && (
              <button
                type="button"
                onClick={() => setZoomed(false)}
                aria-label={tt('Zavrieť zväčšený QR kód')}
                className="fixed inset-0 z-[60] flex items-center justify-center bg-white p-4"
                data-transfer-zoom
              >
                <QRCodeSVG value={codeValue} size={Math.min(560, zoomSize)} level="M" />
              </button>
            )}

            <div
              className={cn(
                'rounded-2xl border p-3 flex items-start gap-2',
                state.phase === 'paid'
                  ? 'border-emerald-500/40 bg-emerald-500/10'
                  : state.phase === 'failed'
                    ? 'border-destructive/40 bg-destructive/10'
                    : 'border-border bg-card',
              )}
              data-transfer-phase={state.phase}
            >
              {state.phase === 'paid' ? (
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />
              ) : state.phase === 'failed' ? (
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-destructive" />
              ) : (
                <Spinner className="h-4 w-4 mt-0.5 shrink-0" />
              )}
              <p className="text-sm font-semibold" data-transfer-headline>
                {headline(state)}
              </p>
            </div>

            {state.phase === 'waiting' && (
              <>
                {/* The server's countdown, not one started here: a deadline held in the browser dies
                  * when the app is backgrounded, which on a driver's phone is immediately. */}
                <p
                  className="text-center text-2xl font-bold tabular-nums"
                  data-transfer-countdown
                  aria-live="polite"
                >
                  {countdown(state.secondsRemaining)}
                </p>
                <p className="text-[11px] text-muted-foreground" data-transfer-waiting>
                  {waitingNote(state)}
                </p>
              </>
            )}

            {/* One button, and it closes. The owner: *"can be only closed"*. */}
            <button
              type="button"
              onClick={props.onClose}
              data-transfer-close
              className={cn(
                'w-full min-h-[52px] rounded-2xl font-bold transition-opacity hover:opacity-90',
                settled && state.phase === 'paid'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border',
              )}
            >
              <T>Zavrieť</T>
            </button>
          </>
        )}
      </section>
    </div>
  )
}
